import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../components/Footer";
import { LockedArtFrame } from "../components/LockedArtFrame";
import { NewsletterSection } from "../components/NewsletterSection";
import { Reveal } from "../components/Reveal";
import { SectionHeading } from "../components/SectionHeading";
import { artAssets, characterBookEntries } from "../data/site";

const wormFieldSheets = [
  {
    title: "ANATOMICAL DISSECTION",
    label: "Maw / Dorsal / Lifecycle / Weak Points",
    image: artAssets.wormAnatomy,
    alt: "Apocalyptic mutated demonic sand worm anatomical character sheet",
  },
  {
    title: "DEMONIC COMBAT SHEET",
    label: "Armor / Sensory Pits / Regenerative Core",
    image: artAssets.wormDemonic,
    alt: "Apocalyptic demonic sand worm combat anatomy sheet",
  },
];

const wormSpeciesSheets = [
  {
    title: "WASTELAND BESTIARY",
    label: "Crag-Maw / Razor-Tail / Burrowing Leech",
    image: artAssets.wormBestiary,
    alt: "Apocalyptic sand-beasts bestiary of the wasteland",
  },
  {
    title: "PREDATOR CLASSIFICATION",
    label: "Rust-Maw / Void-Leech / Blight-Spawn",
    image: artAssets.wormPredators,
    alt: "Bestiarium Apocalyptica predator classification sheet",
  },
];

export default function CharacterBook() {
  const [filter, setFilter] = useState("ALL");
  const [activeId, setActiveId] = useState(characterBookEntries[0].id);
  const filters = useMemo(() => ["ALL", ...new Set(characterBookEntries.map((item) => item.role))], []);
  const entries = useMemo(
    () => (filter === "ALL" ? characterBookEntries : characterBookEntries.filter((item) => item.role === filter)),
    [filter],
  );
  const active = characterBookEntries.find((item) => item.id === activeId) || entries[0] || characterBookEntries[0];
  const activeImage = active.image || null;

  return (
    <main className="character-book-page" data-testid="character-book-page">
      <section className="character-hero">
        <Link to="/" className="back-link" data-testid="character-book-back-link">
          <ArrowLeft size={16} /> RETURN TO WORLD
        </Link>
        <Reveal>
          <p className="eyebrow">TACTICAL INDEX / CHARACTER BOOK</p>
          <h1>THE ARCHIVE IS FULLY STOCKED.</h1>
          <p>
            Official sheets now cover Dolo, Riff, Opus, Goji, Koji, Lil Girl, Drone Boy, the Vinyl Blade Ally, A.I. patrol units, Desert Scout Sphere, Kaelen, the Aether-Grid Audit Division, Black-Ops Net Acoustic Units, their Silent Night joint command, the AL Riotball division units, and mutated wildlife threats.
          </p>
        </Reveal>
      </section>

      <section className="character-index" data-testid="character-index-section">
        <div className="filter-row" aria-label="Character filters">
          {filters.map((item) => (
            <button
              type="button"
              key={item}
              className={filter === item ? "is-active" : ""}
              onClick={() => {
                setFilter(item);
                const first = item === "ALL" ? characterBookEntries[0] : characterBookEntries.find((entry) => entry.role === item);
                if (first) setActiveId(first.id);
              }}
              data-testid={`character-filter-${item.toLowerCase().replaceAll(" ", "-")}`}
            >
              {item}
            </button>
          ))}
        </div>

        <div className="character-book-layout">
          <div className="character-list">
            {entries.map((item, index) => (
              <Reveal key={item.id} delay={index * 0.04}>
                <button
                  type="button"
                  className={`character-row ${active.id === item.id ? "is-active" : ""}`}
                  onClick={() => setActiveId(item.id)}
                  data-testid={`character-row-${item.id}`}
                >
                  <div className={item.image ? "character-row-thumb" : "character-row-icon"}>
                    {item.image ? (
                      <img src={item.image} alt="" data-testid={`character-thumb-${item.id}`} />
                    ) : (
                      <span />
                    )}
                  </div>
                  <div className="character-row-copy">
                    <span>{item.code}</span>
                    <strong>{item.name}</strong>
                    <small>{item.role}</small>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          <Reveal className="character-detail-panel" delay={0.12}>
            {activeImage ? (
              <img
                src={activeImage}
                alt={`${active.name} official character artwork`}
                className={`character-detail-image ${active.imageType === "sheet" ? "is-sheet" : ""}`}
                data-testid="character-detail-image"
              />
            ) : (
              <LockedArtFrame
                code={active.code}
                title={active.status}
                note="Official portrait and character sheet pending."
                className="character-detail-frame"
              />
            )}
            <div className="character-detail-copy" data-testid="active-character-detail">
              <span>{active.status}</span>
              <h2>{active.name}</h2>
              <p>{active.brief}</p>
              <div className="character-metrics">
                <div>
                  <small>FACTION</small>
                  <strong>{active.role}</strong>
                </div>
                <div>
                  <small>POWER RANK</small>
                  <strong>AWAITING FINAL</strong>
                </div>
                <div>
                  <small>ASSET STATE</small>
                  <strong>{activeImage ? (active.imageType === "sheet" ? "SHEET LIVE" : "PORTRAIT LIVE") : "LOCKED"}</strong>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="worm-sheet-section" id="sand-worm-section" data-testid="character-book-worm-dossier">
        <SectionHeading
          eyebrow="MUTATED WILDLIFE / COLOSSAL THREAT"
          title="Demonic sand worm field dossier."
          text="Anatomy, weak points, lifecycle patterns, predator variants, and license requirements for sanctioned wasteland capture operations."
        />
        <Reveal className="worm-hero-media" delay={0.08}>
          <img src={artAssets.wormXray} alt="X-ray diagram of the colossal apocalyptic demonic sand worm" data-testid="sand-worm-xray-image" />
        </Reveal>
      </section>

      <section className="worm-stats-strip" data-testid="sand-worm-stats">
        {[
          ["CLASS", "COLOSSAL PREDATOR"],
          ["PRIMARY HABITAT", "SECTORS 2 / 5 / 7"],
          ["WEAK POINT", "DEMONIC CORE"],
          ["LICENSE FEE", "12,000 CREDITS"],
        ].map(([label, value]) => (
          <div key={label} data-testid={`sand-worm-stat-${label.toLowerCase().replaceAll(" ", "-")}`}>
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </section>

      <section className="worm-sheet-section" data-testid="sand-worm-field-sheets">
        <SectionHeading
          eyebrow="FIELD SHEETS"
          title="Know the maw before you enter the zone."
          text="Official anatomical references cover armor, sensory pits, regenerative tissue, lifecycle stages, and the demonic core."
        />
        <div className="worm-sheet-grid">
          {wormFieldSheets.map((sheet, index) => (
            <Reveal className="worm-sheet-card" delay={index * 0.08} key={sheet.title}>
              <img src={sheet.image} alt={sheet.alt} data-testid={`sand-worm-sheet-${index + 1}`} />
              <div>
                <span>{sheet.label}</span>
                <h3>{sheet.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="worm-sheet-section" data-testid="sand-worm-species-index">
        <SectionHeading
          eyebrow="SPECIES INDEX"
          title="One predator class. Multiple ways to die."
          text="The wasteland bestiary tracks related species, mutation points, scale comparisons, and documented weak points."
        />
        <div className="worm-sheet-grid worm-sheet-grid-wide">
          {wormSpeciesSheets.map((sheet, index) => (
            <Reveal className="worm-sheet-card" delay={index * 0.08} key={sheet.title}>
              <img src={sheet.image} alt={sheet.alt} data-testid={`sand-worm-species-${index + 1}`} />
              <div>
                <span>{sheet.label}</span>
                <h3>{sheet.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="worm-license-panel" data-testid="sand-worm-license-panel">
        <img
          className="worm-license-seal"
          src="/assets/catcher-guild-seal.png"
          alt="A Licensed Sand Worm Catcher — Catcher Guild official seal"
          data-testid="worm-license-seal"
        />
        <Reveal>
          <span>LICENSED CATCHER PROGRAM</span>
          <h2>Become a Licensed Sand Worm Catcher for only 12,000 credits.</h2>
          <p>
            Applications route through the Apex-Courier operator intake for hazard review, route clearance, and safe-zone authorization.
          </p>
          <Link to="/enlist" className="button button-primary" data-testid="sand-worm-license-link">
            BEGIN CATCHER REVIEW <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <NewsletterSection />
      <Footer />
    </main>
  );
}
